<template>
  <listing-layout>
    <div slot="heading" class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">{{ config.title }}</h1>
      <router-link
        v-if="config.addRoute"
        :to="{ name: config.addRoute }"
        class="btn btn-primary d-md-none px-xl-5 mt-2 mt-sm-0"
      >Add<span class="d-none d-sm-inline"> Horse</span></router-link>
    </div>

    <div slot="content">
      <admin-horse-reports v-if="config.variant === 'admin'" />

      <div class="alert alert-success" v-if="alertMessage">{{ alertMessage }}</div>
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>

      <div
        v-if="config.variant === 'member' && hasUnregistered"
        role="alert"
        class="alert alert-info d-lg-flex justify-content-between align-items-center text-center text-lg-left"
      >
        <span>You have unregistered horses.</span>
        <router-link :to="{ name: 'Register Horse' }" class="btn btn-block-mob btn-primary ml-lg-auto mt-2 mt-lg-0">Register Horses</router-link>
      </div>
      <div
        v-if="config.variant === 'member' && canPayRookie"
        role="alert"
        class="alert alert-info justify-content-between align-items-right text-center text-lg-right"
      >
        <router-link :to="{ name: 'Nominate Rookie' }" class="btn btn-block-mob btn-primary">Pay Rookie Horse Nomination</router-link>
      </div>

      <filter-bar :active="hasActiveFilters" @clear="clearAll">
        <filter-multiselect
          label="Zone"
          option-label="code"
          placeholder="Any zone"
          :value="filters.zone_id"
          :options="zoneOptions"
          @input="ids => setFilter('zone_id', ids)"
          @clear="clearFilter('zone_id')"
        />
        <filter-multiselect
          label="Status"
          placeholder="Any status"
          :value="filters.status_id"
          :options="statusOptions"
          @input="ids => setFilter('status_id', ids)"
          @clear="clearFilter('status_id')"
        />
        <filter-multiselect
          label="BoY Status"
          placeholder="Any BoY status"
          :value="filters.boy_status_id"
          :options="statusOptions"
          @input="ids => setFilter('boy_status_id', ids)"
          @clear="clearFilter('boy_status_id')"
        />
        <!-- Active / Archived filter hidden for now; backend + useHorsesList still carry it. -->
      </filter-bar>

      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-0">
          <label for="horsesRegFilter" class="sr-only">Filter</label>
          <div class="filter">
            <div class="form-check pl-0" v-for="tab in tabs" :key="tab.value">
              <input
                class="form-check-input"
                type="radio"
                name="showRegistrationState"
                :id="'showReg-' + tab.value"
                :value="tab.value"
                :checked="filters.registration_state === tab.value"
                @change="onTabChange(tab.value)"
              >
              <label class="form-check-label" :for="'showReg-' + tab.value">{{ tab.label }}</label>
            </div>
          </div>
        </div>

        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
          <label for="horsesSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="horsesSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
          <router-link
            v-if="config.addRoute"
            :to="{ name: config.addRoute }"
            class="btn btn-fz-sm btn-primary d-none d-md-block px-xl-5 mt-2 mt-sm-0 ml-md-0"
          >Add<span class="d-inline"> Horse</span></router-link>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="horses"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No horses found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <list-name-cell
              align="right"
              :label="row.name"
              :to="{ name: config.profileRoute, params: { slug: row.id } }"
            >
              <template #badges>
                <span class="badge" :class="row.active ? 'alert-success' : 'alert-warning'">{{ row.active ? 'Active' : 'Archived' }}</span>
                <span class="badge" :class="row.registrationDate ? 'alert-info' : 'alert-warning'">{{ row.registrationDate ? 'Registered' : 'Unregistered' }}</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:owner_name="{ row }">
            <span>
              <router-link
                v-if="config.variant !== 'member' && row.ownerId"
                :to="{ name: 'Member Profile', params: { slug: row.ownerId } }"
                class="text-primary"
              >{{ row.ownerName }}</router-link>
              <template v-else>{{ row.ownerName }}</template>
              <span v-if="row.ownerDisplayName" class="text-muted"> ({{ row.ownerDisplayName }})</span>
            </span>
          </template>
          <template #cell:zone_id="{ row }">{{ zoneCode(row.zoneId) }}</template>
          <template #cell:beginning_of_year_status="{ row }">{{ row.beginningOfYearStatus }}</template>
          <template #cell:horse_status_id="{ row }">{{ statusName(row.horseStatusId) }}</template>

          <template v-if="config.variant === 'admin'" #cell:remove="{ row }">
            <button
              class="btn btn-sm btn-primary text-nowrap remove-btn"
              :disabled="removingId === row.id"
              @click="askRemove(row)"
            >
              <span v-if="removingId === row.id" class="spinner-border spinner-border-sm text-light" role="status" />
              <span v-else>Remove</span>
            </button>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="horsesShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="horsesShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <modal v-if="horseToRemove" @close="horseToRemove = null">
        <div slot="body">
          <div class="alert alert-warning text-center">
            You are about to remove the horse ‘{{ horseToRemove.name }}’. Are you sure?
          </div>
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-primary" @click="horseToRemove = null">Cancel</button>
            <button class="btn btn-primary ml-2" @click="confirmRemove">Confirm</button>
          </div>
        </div>
      </modal>
    </div>
  </listing-layout>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import modal from '@/components/Modal'
import FilterBar from '@/components/filters/FilterBar'
import FilterMultiselect from '@/components/filters/FilterMultiselect'
import ListNameCell from '@/components/ListNameCell'
import http from '@/services/api/http'
import useHorsesList from '@/composables/horses/useHorsesList'
import AdminHorseReports from './AdminHorseReports'
import { buildColumns, buildBreakpoints, REGISTRATION_TABS } from './columns'

const DEFAULT_CONFIG = {
  title: 'Horses',
  variant: 'secretary',
  profileRoute: 'Secretary Horse Profile',
  addRoute: null
}

export default defineComponent({
  name: 'HorsesListing',
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    modal,
    FilterBar,
    FilterMultiselect,
    ListNameCell,
    AdminHorseReports
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store
    // Computed, not a const: the same component instance is reused when navigating between
    // /horses, /secretary-horses and /admin-horses, so this must re-derive on route change.
    const config = computed(() => ({ ...DEFAULT_CONFIG, ...(proxy.$route.meta.horseList || {}) }))
    const own = computed(() => config.value.variant === 'member')

    const {
      horses, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter, clearFilter, clearFilters, syncFromQuery
    } = useHorsesList(own)

    const columns = computed(() => buildColumns(config.value.variant))
    const breakpoints = computed(() => buildBreakpoints(config.value.variant))
    const tabs = REGISTRATION_TABS

    // Filter dropdown options come from the store (loaded app-wide); Status and BoY Status
    // share the single horse_statuses lookup.
    const zoneOptions = computed(() => store.state.listZones || [])
    const statusOptions = computed(() => store.state.horseStatus || [])

    const hasActiveFilters = computed(() =>
      Boolean(filters.search)
      || filters.registration_state !== 'all'
      || filters.zone_id.length > 0
      || filters.status_id.length > 0
      || filters.boy_status_id.length > 0
    )

    const searchInput = ref(filters.search || '')
    const alertMessage = ref('')
    const errorMessage = ref('')

    function zoneCode(id) {
      const zone = store.state.listZones.find(z => z.id === id)
      return zone ? zone.code : ''
    }
    function statusName(id) {
      const status = store.state.horseStatus.find(s => s.id === id)
      return status ? status.name : ''
    }

    // Search-as-you-type with a 500ms debounce; Enter / the icon fire it immediately.
    let searchTimer = null

    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    function resetSearch() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      setSearch('')
    }

    // "Clear all filters" — wipe search + every dropdown/tab back to defaults in one shot.
    function clearAll() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      clearFilters()
    }

    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })

    onBeforeUnmount(() => clearTimeout(searchTimer))
    function onTabChange(value) {
      setFilter('registration_state', value)
    }

    // --- member-only banners: keep the lightweight legacy summary call, table stays on /v2 ---
    const hasUnregistered = ref(false)
    const canPayRookie = ref(false)

    function loadMemberBanners() {
      if (config.value.variant !== 'member') return
      const role = localStorage.getItem('role')
      http.post('/horse/my-horses', { id: localStorage.getItem('userID') })
        .then((response) => {
          const data = response.data || {}
          if (data.status !== 'success') return
          const familyIds = data.usersIds || []
          const list = data.data || []
          hasUnregistered.value = list.some(h => familyIds.includes(h.owner_id) && !h.registration_date)
          canPayRookie.value = Boolean(data.haveRegisteredHorse) && role !== 'Volunteer'
        })
        .catch(() => { /* banners are best-effort */ })
    }

    // --- admin-only remove ---
    const horseToRemove = ref(null)
    const removingId = ref(null)

    function askRemove(row) {
      horseToRemove.value = row
    }
    function confirmRemove() {
      const row = horseToRemove.value
      horseToRemove.value = null
      removingId.value = row.id
      alertMessage.value = ''
      errorMessage.value = ''
      http.delete(`/admin/horse/${row.id}`)
        .then((response) => {
          const data = response.data || {}
          if (data.errors === true) {
            errorMessage.value = data.message
          } else {
            alertMessage.value = data.message
            fetch()
          }
        })
        .catch(() => {
          errorMessage.value = 'Unable to remove the horse. Please try again later.'
        })
        .finally(() => { removingId.value = null })
    }

    function loadPage() {
      const query = proxy.$route.query
      if (query.success) {
        alertMessage.value = query.success
        const rest = { ...query }
        delete rest.success
        proxy.$router.replace({ query: rest }).catch(() => {})
      }
      fetch()
      loadMemberBanners()
    }

    onMounted(loadPage)

    // Re-load when switching between the shared routes (My Horses <-> Horses <-> Admin Horses)
    // — the component instance is kept, so onMounted won't fire again.
    watch(() => proxy.$route.path, () => {
      clearTimeout(searchTimer)
      syncFromQuery()
      searchInput.value = filters.search || ''
      hasUnregistered.value = false
      canPayRookie.value = false
      alertMessage.value = ''
      errorMessage.value = ''
      loadPage()
    })

    return {
      config,
      columns,
      breakpoints,
      tabs,
      horses,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      alertMessage,
      errorMessage,
      hasUnregistered,
      canPayRookie,
      horseToRemove,
      removingId,
      zoneOptions,
      statusOptions,
      hasActiveFilters,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      clearFilter,
      clearAll,
      applySearch,
      resetSearch,
      onTabChange,
      askRemove,
      confirmRemove,
      zoneCode,
      statusName
    }
  }
})
</script>

<style scoped>
/* Bootstrap's .btn-sm is still sized for a longer label — shrink it further so the Remove
   column doesn't have to reserve as much width, freeing space for Horse Name/Owner. Smaller
   again once the layout gets tight, matching where the table itself starts crowding. */
.remove-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

@media (max-width: 768px) {

  .remove-btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Horses/Listing/HorsesListing.vue